import { absoluteUrl, articleJsonLd, ogImage, seoLinks, clampTitle, clampDescription } from "@/lib/seo";
import { createFileRoute, notFound } from "@tanstack/react-router";

import { BlogArticle } from "@/components/site/BlogViews";
import { CtaBand } from "@/components/site/Page";
import { getPublishedPost } from "@/lib/blog.functions";

export const Route = createFileRoute("/es/blog/$slug")({
  loader: async ({ params }) => {
    const post = await getPublishedPost({ data: { slug: params.slug } });
    if (!post) throw notFound();
    return post;
  },
  head: ({ loaderData }) => {
    const title = clampTitle(loaderData?.seoTitle || loaderData?.title || "Blog – Swedish International School");
    const description = clampDescription(loaderData?.seoDescription || loaderData?.excerpt || "Una entrada de Swedish International School.");
    const path = `/es/blog/${loaderData?.slug ?? ""}`;
    const cover = loaderData?.coverImageUrl || "";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: absoluteUrl(path) },
        { name: "twitter:card", content: "summary_large_image" },
        ...(cover ? ogImage(`${cover}${cover.includes("?") ? "&" : "?"}w=1280`) : ogImage()),
        { property: "og:locale", content: "es_ES" },
      ],
      links: [
        ...seoLinks(path),
        ...(cover
          ? [
              {
                rel: "preload",
                as: "image",
                href: `${cover}${cover.includes("?") ? "&" : "?"}w=1280`,
                fetchpriority: "high",
              },
            ]
          : []),
      ],
      scripts: loaderData
        ? [
            articleJsonLd({
              path,
              headline: loaderData.title,
              description,
              image: loaderData.coverImageUrl || undefined,
              datePublished: loaderData.publishedAt,
              dateModified: loaderData.updatedAt,
              authorName: loaderData.authorName || undefined,
              locale: "es",
              type: "BlogPosting",
            }),
          ]
        : [],
    };
  },
  errorComponent: () => (
    <div className="mx-auto max-w-3xl px-5 py-20 text-sm text-muted-foreground">
      La entrada no se ha podido cargar ahora mismo.
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-5 py-20 text-sm text-muted-foreground">
      Esta entrada ya no está disponible.
    </div>
  ),
  component: BlogPostPage,
});

function BlogPostPage() {
  const post = Route.useLoaderData();
  return (
    <>
      <BlogArticle post={post} locale="es" />
      <CtaBand locale="es" />
    </>
  );
}
